import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPaymentDetailsByAdmin } from 'store/features/orders/orderAction';
import {
  selectPaymentDetailsByAdmin,
  selectPaymentsLoadingByAdmin,
  selectPaymentsErrorByAdmin,
} from 'store/features/orders/orderSelectors';
import { FiCreditCard, FiCalendar, FiCheckCircle, FiClock, FiAlertCircle, FiUser, FiTruck } from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import { format } from 'date-fns';

const Payments = () => {
  const dispatch = useDispatch();
  const paymentDetails = useSelector(selectPaymentDetailsByAdmin);
  const loading = useSelector(selectPaymentsLoadingByAdmin);
  const error = useSelector(selectPaymentsErrorByAdmin);

  useEffect(() => {
    dispatch(fetchPaymentDetailsByAdmin());
  }, [dispatch]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            <FiCheckCircle className="mr-1" /> Paid
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            <FiClock className="mr-1" /> Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800">
            <FiAlertCircle className="mr-1" /> {status}
          </span>
        );
    }
  };

  const getDeliveryStatusBadge = (status) => {
    switch (status) {
      case 'Delivered':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            <FiCheckCircle className="mr-1" /> Delivered
          </span>
        );
      case 'Ordered':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <FiTruck className="mr-1" /> Ordered
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <FiAlertCircle className="mr-1" /> {status}
          </span>
        );
    }
  };

  const calculateTotalRevenue = () => {
    return paymentDetails.reduce((total, payment) => {
      return total + (payment.OrderStatus === 'paid' ? payment.FinalAmount : 0);
    }, 0);
  };

  const calculatePendingRevenue = () => {
    return paymentDetails.reduce((total, payment) => {
      return total + (payment.OrderStatus === 'pending' ? payment.FinalAmount : 0);
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-rose-600 bg-rose-50 rounded-lg border border-rose-100">
        Error loading payments: {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <h1 className="text-2xl font-bold text-indigo-800 mb-4 md:mb-0">Payment Details</h1>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-indigo-50 p-4 rounded-lg shadow-sm border border-indigo-100">
            <div className="text-sm text-indigo-600">Total Payments</div>
            <div className="text-xl font-semibold text-indigo-800">{paymentDetails.length}</div>
          </div>
          <div className="bg-emerald-50 p-4 rounded-lg shadow-sm border border-emerald-100">
            <div className="text-sm text-emerald-600">Paid Revenue</div>
            <div className="text-xl font-semibold text-emerald-800 flex items-center">
              <FaRupeeSign className="mr-1" /> {calculateTotalRevenue().toFixed(2)}
            </div>
          </div>
          <div className="bg-amber-50 p-4 rounded-lg shadow-sm border border-amber-100">
            <div className="text-sm text-amber-600">Pending Revenue</div>
            <div className="text-xl font-semibold text-amber-800 flex items-center">
              <FaRupeeSign className="mr-1" /> 
              {calculatePendingRevenue().toFixed(2)}
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
            <div className="text-sm text-blue-600">Avg. Order Value</div>
            <div className="text-xl font-semibold text-blue-800 flex items-center">
              <FaRupeeSign className="mr-1" /> 
              {paymentDetails.length > 0 
                ? (paymentDetails.reduce((total, payment) => total + payment.FinalAmount, 0) / paymentDetails.length).toFixed(2) 
                : '0.00'}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-indigo-50">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-indigo-100">
            <thead className="bg-indigo-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Amount Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Payment Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Delivery Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-indigo-50">
              {paymentDetails.map((payment) => (
                <tr key={payment.CheckoutId} className="hover:bg-indigo-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-900">
                    #{payment.OrderId || payment.CheckoutId.slice(-6).toUpperCase()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <FiUser className="mr-2 text-indigo-400" />
                      <div>
                        <div className="text-sm font-medium text-indigo-900">
                          {payment.ClientName || 'Customer'}
                        </div>
                        <div className="text-sm text-indigo-600">
                          {payment.ClientEmail}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-700">
                    <div className="flex items-center">
                      <FiCalendar className="mr-2 text-indigo-400" />
                      {format(new Date(payment.Date), 'MMM dd, yyyy')}
                    </div>
                    <div className="text-xs text-indigo-500 mt-1">
                      {format(new Date(payment.Date), 'hh:mm a')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-indigo-900">
                      <div className="flex items-center">
                        <FaRupeeSign className="mr-1 text-indigo-400" />
                        <span className="font-medium">{payment.FinalAmount.toFixed(2)}</span>
                        <span className="text-xs text-indigo-500 ml-2">(Final)</span>
                      </div>
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      <span>Original: {payment.TotalAmount.toFixed(2)}</span>
                      <span className="mx-1 text-indigo-300">•</span>
                      <span>Discount: {payment.DiscountAmount.toFixed(2)}</span>
                    </div>
                    {payment.QuickTransactionId && (
                      <div className="text-xs text-indigo-500 mt-1">
                        Txn: {payment.QuickTransactionId.slice(0, 8)}...
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(payment.OrderStatus)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getDeliveryStatusBadge(payment.DeliveryStatus)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {paymentDetails.length === 0 && !loading && (
        <div className="text-center py-12 bg-indigo-50 rounded-lg border border-indigo-100">
          <FiCreditCard className="mx-auto h-12 w-12 text-indigo-400" />
          <h3 className="mt-2 text-sm font-medium text-indigo-800">No payment records found</h3>
          <p className="mt-1 text-sm text-indigo-600">You don't have any payment records yet.</p>
        </div>
      )}
    </div>
  );
};

export default Payments;